import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCostoPorCategoria } from '@/hooks/useAreaCosteo';
import { formatearCRC } from '@adelante/shared';
import type { CostoPorCategoria } from '@adelante/shared';
import { CircleNotch } from '@phosphor-icons/react';
import type { ReactNode } from 'react';

/**
 * Treemap "Gasto por Categoría" — agrupa costos por item_category_code
 * de dim_codigo (CONCRETO_PREMEZ, CEMENTO_MORTEROS, VARILLA_MALLA, etc.).
 *
 * Componente compartido entre AreaCosteoPantalla y CostosDetalladosPantalla.
 * Usa el hook useCostoPorCategoria que respeta los filtros globales
 * (incluido excluirIndirectos / incluirSSCC).
 *
 * Treemap implementado con CSS grid: el área de cada celda es proporcional
 * a la raíz cuadrada del % (aproximación visual de treemap sin dependencia
 * externa de recharts).
 *
 * Interactividad opcional (usada en Costos Detallados):
 * - `seleccion`: si se pasa un Set, solo se renderizan esas categorías y los
 *   porcentajes se recalculan sobre ese subconjunto (ej. "todas menos Cuenta"
 *   para ver el pastel sin las cuentas). `null`/`undefined` = todas.
 * - `enfocada` + `onEnfocar`: hace las celdas clickeables. Al enfocar una
 *   categoría, las demás se pintan en gris (y el consumidor filtra su tabla).
 */

const COLORES = [
  'bg-red-200',
  'bg-orange-200',
  'bg-amber-200',
  'bg-yellow-200',
  'bg-lime-200',
  'bg-green-200',
  'bg-emerald-200',
  'bg-teal-200',
  'bg-cyan-200',
  'bg-sky-200',
  'bg-blue-200',
  'bg-indigo-200',
  'bg-violet-200',
  'bg-purple-200',
  'bg-fuchsia-200',
  'bg-pink-200',
  'bg-rose-200',
  'bg-stone-200',
  'bg-slate-200',
  'bg-neutral-200',
];

interface TreemapCategoriasProps {
  titulo?: string;
  /** Categorías visibles (recalcula %). undefined/null = todas. */
  seleccion?: Set<string> | null;
  /** Categoría enfocada: las demás se pintan en gris. */
  enfocada?: string | null;
  /** Si se provee, las celdas son clickeables (toggle de enfoque). */
  onEnfocar?: (itemCategoryCode: string) => void;
  /** Contenido extra en el header (ej. dropdown de selección). */
  acciones?: ReactNode;
}

export function TreemapCategorias({
  titulo = 'Gasto por Categoría',
  seleccion,
  enfocada,
  onEnfocar,
  acciones,
}: TreemapCategoriasProps) {
  const { data, isLoading, error } = useCostoPorCategoria();

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 pt-6 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
          Cargando treemap…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const todos = data?.data ?? [];
  // Solo categorías visibles (la selección del dropdown). Los % se recalculan
  // sobre este subconjunto para que el pastel "se reacomode" sin las ocultas.
  const items = seleccion ? todos.filter((i) => seleccion.has(i.itemCategoryCode)) : todos;
  const total = items.reduce((acc, i) => acc + i.totalCostoLcy, 0);
  const interactivo = typeof onEnfocar === 'function';

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3 space-y-0">
        <CardTitle className="text-sm font-medium">{titulo}</CardTitle>
        {acciones}
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin datos.</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {items.map((i, idx) => (
              <CeldaTreemap
                key={i.itemCategoryCode}
                item={i}
                idx={idx}
                total={total}
                enfocada={enfocada ?? null}
                interactivo={interactivo}
                onEnfocar={onEnfocar}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Una celda del treemap. Se extrae a componente para que dentro del `.map`
 * solo exista un elemento con `key` (evita el falso positivo de
 * useJsxKeyInIterable con el return ternario button/div).
 */
function CeldaTreemap({
  item,
  idx,
  total,
  enfocada,
  interactivo,
  onEnfocar,
}: {
  item: CostoPorCategoria;
  idx: number;
  total: number;
  enfocada: string | null;
  interactivo: boolean;
  onEnfocar?: (itemCategoryCode: string) => void;
}) {
  const pct = total > 0 ? (item.totalCostoLcy / total) * 100 : 0;
  const width = Math.max(60, Math.sqrt(pct) * 30);
  const height = Math.max(40, Math.sqrt(pct) * 20);
  const esEnfocada = enfocada != null && enfocada === item.itemCategoryCode;
  // En gris si hay una categoría enfocada y NO es esta.
  const apagada = enfocada != null && !esEnfocada;
  const colorBase = COLORES[idx % COLORES.length];
  const clases = [
    apagada ? 'bg-zinc-100 text-zinc-400 grayscale opacity-60' : colorBase,
    esEnfocada ? 'ring-2 ring-blue-500 ring-offset-1' : '',
    'flex flex-col justify-between rounded p-2 transition',
    interactivo ? 'cursor-pointer hover:scale-105 hover:ring-1 hover:ring-zinc-400' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const titleAttr = `${item.itemCategoryCode}: ${formatearCRC(item.totalCostoLcy)} (${pct.toFixed(1)}%)${
    interactivo ? ' · click para enfocar' : ''
  }`;
  const contenido = <ContenidoCelda code={item.itemCategoryCode} pct={pct} />;

  return interactivo ? (
    <button
      type="button"
      className={`${clases} text-left`}
      style={{ width: `${width}px`, height: `${height}px` }}
      title={titleAttr}
      onClick={() => onEnfocar?.(item.itemCategoryCode)}
      aria-pressed={esEnfocada}
    >
      {contenido}
    </button>
  ) : (
    <div
      className={clases}
      style={{ width: `${width}px`, height: `${height}px` }}
      title={titleAttr}
    >
      {contenido}
    </div>
  );
}

/** Contenido interno de una celda del treemap (código + porcentaje). */
function ContenidoCelda({ code, pct }: { code: string; pct: number }) {
  return (
    <>
      <div className="text-[10px] font-semibold leading-tight truncate">{code}</div>
      <div className="text-[9px] text-zinc-700 tabular-nums">{pct.toFixed(1)}%</div>
    </>
  );
}
